import { useAuthStore } from "../stores/auth-store";
import { clearProactiveRefresh } from "./auth-service";
import { env } from "./env";
import { attemptRefresh } from "./refresh-mutex";

const BASE_URL = env.API_BASE_URL;

class ApiClientError extends Error {
  status: number;
  errors: string[];
  data?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    errors: string[] = [],
    data?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.errors = errors;
    this.data = data;
  }
}

export type ApiRequestInit = RequestInit & {
  _skipAuth?: boolean;
  _isRetry?: boolean;
};

// ---- Response parsing helper ----
async function handleResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(text);
  } catch {
    if (!response.ok) {
      throw new ApiClientError(response.statusText, response.status);
    }
    return text as unknown as T;
  }

  if (!response.ok) {
    const errors = Array.isArray(data.errors)
      ? (data.errors as string[])
      : Array.isArray(data.error)
        ? (data.error as string[])
        : typeof data.error === "string"
          ? [data.error]
          : [];

    throw new ApiClientError(
      (data.message as string) ?? response.statusText,
      response.status,
      errors,
      data
    );
  }

  return data as T;
}

// ---- Core request function ----
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init?: ApiRequestInit
): Promise<T> {
  const { _skipAuth = false, _isRetry = false, ...fetchInit } = init ?? {};
  const headers = new Headers(fetchInit.headers);

  if (body && !(body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  // Auto-attach Bearer token unless _skipAuth is set
  if (!_skipAuth) {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...fetchInit,
    method,
    credentials: "include",
    headers,
    body: body
      ? body instanceof FormData
        ? body
        : JSON.stringify(body)
      : undefined,
  });

  // 401 interceptor — attempt refresh and retry once
  if (response.status === 401 && !_skipAuth && !_isRetry) {
    const refreshed = await attemptRefresh();

    if (refreshed) {
      return request<T>(method, path, body, { ...init, _isRetry: true });
    }

    // Refresh failed — clear proactive timer and auth state
    clearProactiveRefresh();
    useAuthStore.getState().clearAuth();
    throw new ApiClientError("Session expired", 401);
  }

  return handleResponse<T>(response);
}

export const apiClient = {
  get<T>(path: string, init?: ApiRequestInit) {
    return request<T>("GET", path, undefined, init);
  },

  post<T>(path: string, body?: unknown, init?: ApiRequestInit) {
    return request<T>("POST", path, body, init);
  },

  put<T>(path: string, body?: unknown, init?: ApiRequestInit) {
    return request<T>("PUT", path, body, init);
  },

  patch<T>(path: string, body?: unknown, init?: ApiRequestInit) {
    return request<T>("PATCH", path, body, init);
  },

  delete<T>(path: string, init?: ApiRequestInit) {
    return request<T>("DELETE", path, undefined, init);
  },
};

export { ApiClientError };
